import { z } from 'zod';

// Geometry rendered as either `{ lat, lng }` (the canonical normalised
// form) or a PostGIS geometry tuple. The tuple is the `[x, y]` = `[lng, lat]`
// form returned by Drizzle's `geometry({ type: 'point' })` adapter (lng-first,
// matching `ST_MakePoint(lng, lat)` storage) — NOT lat-first. Mirrors
// hotelListItemSchema.locationPoint in `./products.ts` so consumers don't need
// a second normalisation path.
const locationPointSchema = z
  .object({ lat: z.number(), lng: z.number() })
  .or(z.tuple([z.number(), z.number()]))
  .nullable();

// Mirrors legacy FindHyperguestHotelDto extends PaginationInputDto
// (`src/external/hyperguest/dto/find-hyperguest-hotel.dto.ts`).
export const findHyperguestHotelsInputSchema = z.object({
  countries: z.array(z.string()).optional(),
  search: z.string().optional(),
  limit: z.number().int().positive().max(500).default(100),
  offset: z.number().int().nonnegative().default(0),
});
export type FindHyperguestHotelsInput = z.infer<
  typeof findHyperguestHotelsInputSchema
>;

// Single hotel row — shape returned by `find` and as the `hyperguestHotel`
// side of the `findMatched` / `findUnconfirmed` joined response.
export const hyperguestHotelSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  country: z.string().nullable(),
  city: z.string().nullable(),
  streetAddress: z.string().nullable(),
  externalId: z.number().int(),
  chainId: z.number().int().nullable(),
  locationPoint: locationPointSchema,
  // ISO timestamp string (Drizzle returns timestamp({ mode: 'string' })).
  createdOn: z.string(),
});
export type HyperguestHotel = z.infer<typeof hyperguestHotelSchema>;

// Product hotel row shape — subset of productInProducts that the legacy
// /hyperguest-hotels/{matched,unconfirmed,products} endpoints projected.
// Kept narrow so the mapper page only sees what it needs.
export const hyperguestProductHotelSchema = z.object({
  id: z.uuid(),
  title: z.unknown(),
  country: z.string().nullable(),
  city: z.string().nullable(),
  locationPoint: locationPointSchema,
  legs: z.unknown(),
  hyperGuestHotelId: z.number().int().nullable(),
  unconfirmedHyperGuestId: z.number().int().nullable(),
});
export type HyperguestProductHotel = z.infer<
  typeof hyperguestProductHotelSchema
>;

// Joined product+hotel row — shape returned by `findMatched` and
// `findUnconfirmed`. Mirrors legacy mapProductWithHotel.
export const productWithHyperguestHotelSchema = z.object({
  productHotel: hyperguestProductHotelSchema,
  hyperguestHotel: hyperguestHotelSchema,
});
export type ProductWithHyperguestHotel = z.infer<
  typeof productWithHyperguestHotelSchema
>;

// Pagination wrappers — `{ data, count, countAll }` matches the legacy
// PaginationOutputDto<T> shape used by every other read endpoint in
// `apps/products/api/src/products/`.
export const paginatedHyperguestHotelsSchema = z.object({
  data: z.array(hyperguestHotelSchema),
  count: z.number().int(),
  countAll: z.number().int(),
});
export type PaginatedHyperguestHotels = z.infer<
  typeof paginatedHyperguestHotelsSchema
>;

export const paginatedProductWithHyperguestHotelSchema = z.object({
  data: z.array(productWithHyperguestHotelSchema),
  count: z.number().int(),
  countAll: z.number().int(),
});
export type PaginatedProductWithHyperguestHotel = z.infer<
  typeof paginatedProductWithHyperguestHotelSchema
>;

export const paginatedHyperguestProductHotelsSchema = z.object({
  data: z.array(hyperguestProductHotelSchema),
  count: z.number().int(),
  countAll: z.number().int(),
});
export type PaginatedHyperguestProductHotels = z.infer<
  typeof paginatedHyperguestProductHotelsSchema
>;

// Sync inputs / outputs ----------------------------------------------------

export const hyperguestImportInputSchema = z.object({
  // Mirrors the legacy CLI -l flag. Capped at 10k so a fat-finger doesn't
  // request the full ~50k catalogue with 200ms-per-row latency.
  limit: z.number().int().positive().max(10_000).optional(),
});
export type HyperguestImportInput = z.infer<typeof hyperguestImportInputSchema>;

export const hyperguestImportResultSchema = z.object({
  successCount: z.number().int(),
  failCount: z.number().int(),
  // Total hotels in the upstream list (informational — the actual
  // candidates after the existing-id diff is `successCount + failCount`).
  total: z.number().int(),
});
export type HyperguestImportResult = z.infer<
  typeof hyperguestImportResultSchema
>;

export const hyperguestMatchResultSchema = z.object({
  matchCount: z.number().int(),
  batches: z.number().int(),
});
export type HyperguestMatchResult = z.infer<typeof hyperguestMatchResultSchema>;

// ─── applyMatches ─────────────────────────────────────────────────────────
//
// Bulk-applies confirm / reject / manual-map intents from the admin Mapper.
// Replaces the legacy `PATCH /products/bulk` for this specific use case
// without exposing a generic bulk-product-update surface.

export const applyHyperguestMatchInputItemSchema = z.object({
  productId: z.uuid(),
  // `null` clears the column. Setting `hyperGuestHotelId` to a number
  // also clears `unconfirmedHyperGuestId` (promotion semantics).
  hyperGuestHotelId: z.number().int().nullable(),
  unconfirmedHyperGuestId: z.number().int().nullable().optional(),
});
export type ApplyHyperguestMatchInputItem = z.infer<
  typeof applyHyperguestMatchInputItemSchema
>;

export const applyHyperguestMatchesInputSchema = z.object({
  items: z.array(applyHyperguestMatchInputItemSchema).min(1).max(500),
});
export type ApplyHyperguestMatchesInput = z.infer<
  typeof applyHyperguestMatchesInputSchema
>;

export const applyHyperguestMatchesResultSchema = z.object({
  affected: z.number().int(),
});
export type ApplyHyperguestMatchesResult = z.infer<
  typeof applyHyperguestMatchesResultSchema
>;

// ─── suggestMatches ───────────────────────────────────────────────────────
//
// LLM-scored similarity between one "subject" hotel and a candidate list.
// Direction lets the same procedure power "product → HG" and "HG → product"
// suggestions from the mapper. Server-side so the API key never leaves the
// API process. Goes via OpenRouter (same gateway agent-panel uses).

const hyperguestSuggestHotelSchema = z.object({
  id: z.string(),
  title: z.string(),
  country: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  streetAddress: z.string().nullable().optional(),
});

export const suggestHyperguestMatchesInputSchema = z.object({
  direction: z.enum(['product-to-hg', 'hg-to-product']),
  subject: hyperguestSuggestHotelSchema,
  candidates: z.array(hyperguestSuggestHotelSchema).min(1).max(50),
  // Number of top suggestions to return; cap matches legacy `topK=10`.
  topK: z.number().int().min(1).max(20).default(10),
});
export type SuggestHyperguestMatchesInput = z.infer<
  typeof suggestHyperguestMatchesInputSchema
>;

export const suggestHyperguestMatchesResultSchema = z.object({
  suggestions: z.array(
    z.object({
      candidateId: z.string(),
      // 0..1 — same convention as the legacy client-side embedding cosine.
      similarity: z.number().min(0).max(1),
    })
  ),
});
export type SuggestHyperguestMatchesResult = z.infer<
  typeof suggestHyperguestMatchesResultSchema
>;

// ─── search (availability) ────────────────────────────────────────────────
//
// Proxies the upstream `https://search-api.hyperguest.io/` query to keep
// HYPERGUEST_API_KEY server-side. The full response shape is wide and
// fully owned by the third party, so we accept it as `unknown` at the
// schema boundary and let the UI shape it as needed (the legacy code does
// the same — see `types/hyper-guest-search-result.ts`).

export const hyperguestSearchInputSchema = z.object({
  // Legacy searched every mapped hotel in one request (`allHotelIds`); the
  // planner availability view does the same so it can sort by availability
  // across the whole list. No upper cap — matches legacy behaviour.
  hotelIds: z.array(z.string()).min(1),
  // ISO date string `YYYY-MM-DD`. The HG API expects it in that exact form.
  checkIn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  nights: z.number().int().min(1).max(60),
  guests: z.number().int().min(1).max(20),
});
export type HyperguestSearchInput = z.infer<typeof hyperguestSearchInputSchema>;

export const hyperguestSearchResultSchema = z.object({
  // Pass-through — see legacy `types/hyper-guest-search-result.ts` for the
  // expected nested room/ratePlan shape. Kept unknown intentionally so a
  // third-party schema drift doesn't break the API at validate-time.
  results: z.array(z.unknown()),
});
export type HyperguestSearchResult = z.infer<
  typeof hyperguestSearchResultSchema
>;

// ─── booking (book / cancel) + static content ───────────────────────────────
//
// Ports of legacy `POST /api/hyper-guest/book`, `POST /api/hyper-guest/cancel`
// and `GET /api/hyper-guest/static`. Like `search`, these proxy a third-party
// API whose response shape is wide and owned by HyperGuest, so the *response*
// schemas are permissive — they type only the fields the UI reads and let the
// rest pass through (the router omits `.output()` for the same reason). The
// *input* schemas mirror the legacy request types field-for-field (see
// `travel_planner_front/types/hyper-guest-search-result.ts`).

export const hyperguestBookStatusSchema = z.enum([
  'Confirmed',
  'Pending',
  'Cancelled',
  'Rejected',
  'Failed',
]);
export type HyperguestBookStatus = z.infer<typeof hyperguestBookStatusSchema>;

const hyperguestContactSchema = z.object({
  address: z.string(),
  city: z.string(),
  country: z.string(),
  email: z.string(),
  phone: z.string(),
  state: z.string(),
  zip: z.string(),
});
const hyperguestNameSchema = z.object({
  first: z.string(),
  last: z.string(),
});
const hyperguestMoneySchema = z.object({
  amount: z.number(),
  currency: z.string(),
});

// Faithful mirror of legacy `HyperGuestBookRequest`. Forwarded verbatim to
// `POST {HYPERGUEST_BOOK_BASE_URL}/2.0/booking/create`.
export const hyperguestBookInputSchema = z.object({
  dates: z.object({ from: z.string(), to: z.string() }),
  propertyId: z.number().int(),
  leadGuest: z.object({
    birthDate: z.string(),
    contact: hyperguestContactSchema,
    name: hyperguestNameSchema,
    title: z.string(),
  }),
  reference: z.object({ agency: z.string() }),
  paymentDetails: z.object({
    type: z.string(),
    details: z.object({
      number: z.string(),
      cvv: z.string(),
      expiry: z.object({ month: z.string(), year: z.string() }),
      name: hyperguestNameSchema,
    }),
  }),
  rooms: z
    .array(
      z.object({
        roomCode: z.string(),
        rateCode: z.string(),
        expectedPrice: hyperguestMoneySchema,
        guests: z.array(
          z.object({
            birthDate: z.string(),
            contact: hyperguestContactSchema.optional(),
            name: hyperguestNameSchema,
            title: z.string(),
          })
        ),
        specialRequests: z.array(z.string()).optional(),
      })
    )
    .min(1),
  meta: z.array(z.object({ key: z.string(), value: z.string() })).optional(),
  isTest: z.boolean(),
  groupBooking: z.boolean().optional(),
});
export type HyperguestBookInput = z.infer<typeof hyperguestBookInputSchema>;

// Permissive — the UI reads `content.status` + `content.bookingId`; the rest
// of the upstream payload passes through untyped (never `.parse()`d).
export const hyperguestBookResponseSchema = z.object({
  content: z.object({
    status: hyperguestBookStatusSchema,
    bookingId: z.union([z.number(), z.string()]).optional(),
  }),
});
export type HyperguestBookResponse = z.infer<
  typeof hyperguestBookResponseSchema
>;

// Mirror of legacy `HyperGuestCancelRequest`. Forwarded to
// `POST {HYPERGUEST_BOOK_BASE_URL}/2.0/booking/cancel`.
export const hyperguestCancelInputSchema = z.object({
  bookingId: z.number().int(),
  reason: z.string(),
});
export type HyperguestCancelInput = z.infer<typeof hyperguestCancelInputSchema>;

export const hyperguestStaticInputSchema = z.object({
  hotelId: z.number().int().positive(),
});
export type HyperguestStaticInput = z.infer<typeof hyperguestStaticInputSchema>;

// Room images/descriptions from `{hotelId}/property-static.json`. Permissive
// pass-through; the UI reads `rooms[].images[].uri` + `rooms[].descriptions`.
export const hyperguestStaticResultSchema = z.object({
  rooms: z.array(z.unknown()),
});
export type HyperguestStaticResult = z.infer<
  typeof hyperguestStaticResultSchema
>;
